import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react'

import type { PopoverContentProps } from './popover.types'

import Box from '../box'
import { useStyleConfig, vui } from '../core'
import { zIndices } from '../theme'
import { cs, mergeRefs } from '../utils'
import { usePopoverContext } from './context'

/** Displays provided content in an overlaying wrapper. */
export const PopoverContent = vui<'div', PopoverContentProps>((props, ref) => {
  const { children, className, ...rest } = props
  const popoverProps = usePopoverContext() ?? {}
  const { context, floatingStyles, getFloatingProps, isLazy, isMounted, isOpen, setPopper, transitionStyles } =
    popoverProps
  const styles = useStyleConfig('Popover', { ...popoverProps, ...props })

  const shouldRenderChildren = isLazy ? isOpen : true

  // Use default popover z-index
  const zIndex = zIndices.popover

  // Use isMounted for render control - handles transition timing
  if (!isMounted) {
    return null
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
        <Box
          bg="white"
          borderRadius="md"
          boxShadow="2"
          className={cs('vui-popoverContent', className)}
          overflowY="auto"
          ref={mergeRefs(setPopper, ref)}
          ring={0}
          style={{ ...floatingStyles, ...transitionStyles, zIndex }}
          {...(getFloatingProps?.() ?? {})}
          {...styles.content}
          {...rest}
        >
          {shouldRenderChildren ? children : null}
        </Box>
      </FloatingFocusManager>
    </FloatingPortal>
  )
})

PopoverContent.displayName = 'PopoverContent'

export default PopoverContent
